import React from "react";
import { graphql, useFragment } from "react-relay";
import { cn } from "src/common/utils";
import { Markdown } from "src/components/ui/Markdown";
import { Shimmer } from "src/components/ui/shimmer";

import { JobRunOutput_jobRun$key } from "./__generated__/JobRunOutput_jobRun.graphql";
import { AlertResponderJobRun } from "./AlertResponderJobRun";

// Define the fragment that specifies what data this component needs
export const jobRunOutputFragment = graphql`
  fragment JobRunOutput_jobRun on GraphQLJobRun {
    id
    status
    outputJson
    sources {
      sourceId
      toolId
      query
      link
      artifactId
      type
      displayText

    }
    ...AlertResponderJobRun_jobRun
  }
`;

export interface JobRunOutputProps {
  /** The job run data */
  jobRun: JobRunOutput_jobRun$key;
  /** ID used for shimmer effect when loading */
  shimmerSeed?: string;
  /** Additional CSS classes */
  className?: string;
  /** Task type to determine rendering mode */
  taskType?: string;
}

export function JobRunOutput({
  jobRun,
  shimmerSeed = "loading",
  className,
  taskType,
}: JobRunOutputProps) {
  // Use fragment to extract data
  const data = useFragment(jobRunOutputFragment, jobRun);

  // Extract and process data from fragment
  const sources = data.sources || [];
  const outputJson = data.outputJson || {};
  const isInProgress = data.status === "IN_PROGRESS";

  // Get content to display - maintain original logic
  const outputContent =
    typeof outputJson === "string"
      ? outputJson
      : outputJson?.result || outputJson?.partial_result;

  // Check if we're loading with no content yet
  const isLoading = isInProgress && !outputContent;

  // Digests render their own sections via DigestSectionAccordion
  if (taskType === "DIGEST") {
    return null;
  }

  // For AGENT task types, use the AlertResponderJobRun component with refetching
  if (taskType === "ALERT_RESPONDER") {
    return (
      <div className={cn("relative", className)}>
        <AlertResponderJobRun jobRun={data} />
      </div>
    );
  }

  return (
    <div className={cn("relative", className)}>
      <div className="relative rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="relative rounded-lg overflow-hidden">
            <div className="py-4 px-2 rounded-lg">
              <Shimmer lines={3} seed={shimmerSeed || data.id} />
            </div>
          </div>
        ) : (
          <Markdown
            sources={sources.map((source) => ({
              sourceId: String(source.sourceId),
              toolId: source.toolId === null ? undefined : source.toolId,
              query: source.query ? String(source.query) : undefined,
              link: source.link === null ? undefined : source.link,
              artifactId:
                source.artifactId === null ? undefined : source.artifactId,
              type: source.type === null ? undefined : source.type,
              displayText: source.displayText || "",
            }))}
          >
            {outputContent}
          </Markdown>
        )}
      </div>
    </div>
  );
}
